/**
 * Take a tour - story-style walkthrough of PriceGuard NZ for first-time
 * visitors. Linked from the Landing hero as a secondary CTA. Eight
 * sections walk the user through the three lanes, a refund moment, the
 * vault, and the bonus/promo alerts, ending in a free signup CTA.
 *
 * Stylised mock UI panels rather than real screenshots so the tour
 * doesn't drift out of date when the real UI is tweaked. Each panel uses
 * the same colour tokens and type scale as the real product, so the
 * tour still looks like the app.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Receipt,
  Heart,
  ShieldCheck,
  ArrowDown,
  Gift,
  TagSimple,
  Bell,
  Clock,
  CheckCircle,
  EnvelopeSimple,
  Sparkle,
} from "@phosphor-icons/react";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";

export default function Tour() {
  const { user } = useAuth();

  // Smooth scroll to top on mount - tour pages always start at section 01.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="tour-page">
      <Header />

      <main>
        <TourHero />

        <TourSection
          lane="Your control centre"
          accent="#002FA7"
          title="One place to watch everything you own"
          body="PriceGuard NZ is a quiet helper that sits on top of your purchases - the ones you've already made, and the ones you're thinking about. Three lanes, one dashboard. You won't have to chase anything yourself."
        >
          <ServiceHubMock />
        </TourSection>

        <TourSection
          lane="Tracked purchases"
          accent="#002FA7"
          title="Lane one: tracked purchases"
          body="Paste a link or upload a receipt from PB Tech, JB Hi-Fi or Harvey Norman. We pull the price, product details, and warranty window automatically. From that moment on, we watch the listing for you - every single day."
          reverse
        >
          <TrackedMock />
        </TourSection>

        <TourSection
          lane="The refund moment"
          accent="#16A34A"
          title="The refund moment"
          body="If the listed price drops below what you paid, inside the retailer's published window, we draft a polite, retailer-ready email asking for the difference back. You read it, copy it, send it. One refund a year is free for everyone."
        >
          <RefundMomentMock />
        </TourSection>

        <TourSection
          lane="Wishlist"
          accent="#16A34A"
          title="Lane two: wishlist"
          body="Track products you haven't bought yet. Set a target price - we'll email the moment it lands, and quietly suggest cross-retailer matches if the same product shows up cheaper elsewhere."
          reverse
        >
          <WishlistMock />
        </TourSection>

        <TourSection
          lane="The vault"
          accent="#D97706"
          title="Lane three: the vault"
          body="Your receipts and warranties, safe and searchable. We read each receipt automatically so the right details land in the right boxes - no manual typing. Hand it to your insurer as a household asset register whenever they ask."
        >
          <VaultMock />
        </TourSection>

        <TourSection
          lane="Promos and bonuses"
          accent="#D97706"
          title="Promo codes and bonus offers, surfaced"
          body="When a retailer adds a checkout code, a free Sonos with the TV, store credit with a fridge, or a cashback on an appliance, we spot it on the page and email you. These don't move the listed price, so they're easy to miss without us."
          reverse
        >
          <PromoBonusMock />
        </TourSection>

        <TourSection
          lane="Sale countdowns"
          accent="#002FA7"
          title="Sale countdowns, on the dashboard"
          body="When a tracked item is in a sale window with only a couple of days left, it pops to the top of your dashboard with a time-pressure strip. You'll never be the person who hears about the discount one day after it ended."
        >
          <SaleEndingMock />
        </TourSection>

        <TourSection
          lane="Quiet by design"
          accent="#16A34A"
          title="Quiet by design"
          body="No daily emails. No app to open. PriceGuard runs in the background and only ever pings you when there's something worth acting on - a refund, a warranty about to expire, a real bonus on a real item you're tracking."
          reverse
        >
          <QuietMock />
        </TourSection>

        <TourClosing user={user} />
      </main>
    </div>
  );
}

// ----- Hero -----

function TourHero() {
  return (
    <section className="bg-[#0a0a0a] text-white" data-testid="tour-hero">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="pg-overline text-[#D97706]">Take a tour · 5 minutes</div>
        <h1 className="font-heading text-5xl lg:text-7xl font-black tracking-tighter mt-4 leading-[1.02]">
          See PriceGuard NZ<br className="hidden md:block" />
          in one quick scroll<span className="text-[#D97706]">.</span>
        </h1>
        <p className="text-base lg:text-lg text-white/75 leading-relaxed mt-6 max-w-2xl">
          No signup required. We'll walk you through how we watch your purchases, file price adjustment claims for you, keep your receipts safe, and quietly flag the bonuses and codes that other Kiwis miss. Use any part of it - we don't mind which.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href="#tour-your-control-centre"
            className="bg-[#002FA7] text-white font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-[#00207A] transition-colors"
            data-testid="tour-start-btn"
          >
            Start the tour <ArrowRight size={16} weight="bold" />
          </a>
          <Link
            to="/signup?ref=tour"
            onClick={() => { try { if (window.gtag) window.gtag("event", "tour_signup_cta_click", { from: "tour_hero" }); } catch { /* no-op */ } }}
            className="border border-white/30 text-white font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-white hover:text-black transition-colors"
            data-testid="tour-skip-signup-btn"
          >
            Skip ahead, take it for a spin
          </Link>
        </div>
        <p className="text-[11px] text-white/40 mt-6 font-mono-pg uppercase tracking-widest">
          Pro is free for everyone during launch · no card needed
        </p>
      </div>
    </section>
  );
}

// ----- Section wrapper -----

function TourSection({ lane, accent, title, body, children, reverse }) {
  // Slug the lane for both the anchor id and the data-testid so each
  // section is still individually addressable in tests + share links,
  // without exposing brittle step numbers in the URL or copy.
  const slug = lane.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return (
    <section
      id={`tour-${slug}`}
      className="border-t border-[#e4e4e7]"
      data-testid={`tour-section-${slug}`}
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}>
          <div className="lg:col-span-5">
            <div className="pg-overline" style={{ color: accent }}>{lane}</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight mt-3 leading-tight">
              {title}
            </h2>
            <p className="text-base text-[#52525b] leading-relaxed mt-5">
              {body}
            </p>
            {/* Off-ramp - a quiet "you can sign up from anywhere" affordance
                in every section, so a sold-on-the-spot visitor doesn't have
                to scroll to the bottom to convert. */}
            <Link
              to="/signup?ref=tour"
              onClick={() => { try { if (window.gtag) window.gtag("event", "tour_signup_cta_click", { from: slug }); } catch { /* no-op */ } }}
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#002FA7] hover:text-[#00207A] border-b border-[#002FA7]/30 hover:border-[#002FA7] pb-0.5 transition-colors"
              data-testid={`tour-section-${slug}-signup`}
            >
              Sold? Take it for a spin, free <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <div className="bg-[#f7f7f8] border border-[#e4e4e7] p-6 lg:p-8 relative" data-testid={`tour-mock-${slug}`}>
              {children}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ----- Closing -----

function TourClosing({ user }) {
  return (
    <section className="bg-gradient-to-br from-[#002FA7] to-[#001A5C] text-white border-t border-[#e4e4e7]" data-testid="tour-closing">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="pg-overline text-[#D97706]">That's the tour</div>
        <h2 className="font-heading text-4xl lg:text-6xl font-black tracking-tighter mt-4 leading-[1.05]">
          Take it for a spin, free<span className="text-[#D97706]">.</span>
        </h2>
        <p className="text-base lg:text-lg text-white/85 leading-relaxed mt-6 max-w-2xl">
          Pro is free for every signed-up user during launch. No card, no payment, no trial countdown. Add your first purchase or wishlist item in under a minute and let PriceGuard do the watching from there.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          {user ? (
            <Link
              to="/dashboard"
              className="bg-[#D97706] text-white font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-[#B86404] transition-colors"
              data-testid="tour-closing-dashboard-btn"
            >
              Open your dashboard <ArrowRight size={16} weight="bold" />
            </Link>
          ) : (
            <Link
              to="/signup?ref=tour"
              onClick={() => { try { if (window.gtag) window.gtag("event", "tour_signup_cta_click"); } catch { /* no-op */ } }}
              className="bg-[#D97706] text-white font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-[#B86404] transition-colors"
              data-testid="tour-closing-signup-btn"
            >
              Take it for a spin, free <ArrowRight size={16} weight="bold" />
            </Link>
          )}
          <Link
            to="/faq"
            className="border border-white/30 text-white font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-white hover:text-black transition-colors"
            data-testid="tour-closing-faq-btn"
          >
            Got questions? See the FAQ
          </Link>
        </div>
        <p className="text-[11px] text-white/50 mt-8 font-mono-pg uppercase tracking-widest">
          Built in Aotearoa · Pro free during launch · cancel anytime, never lock-in
        </p>
      </div>
    </section>
  );
}

// ============================================================
// MOCK PANELS — stylised facsimiles of real product surfaces.
// Kept deliberately simple so they age well as the real UI evolves.
// ============================================================

function ServiceHubMock() {
  const cards = [
    { lane: "Lane 1", title: "Tracked purchases", icon: Receipt, accent: "#002FA7", count: 12, label: "items being watched" },
    { lane: "Lane 2", title: "Wishlist", icon: Heart, accent: "#16A34A", count: 5, label: "items being watched" },
    { lane: "Lane 3", title: "Vault", icon: ShieldCheck, accent: "#D97706", count: 9, label: "assets filed" },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <div key={c.lane} className="bg-white p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="pg-overline" style={{ color: c.accent }}>{c.lane}</div>
                <div className="font-heading text-lg font-black tracking-tight mt-1">{c.title}</div>
              </div>
              <div className="w-9 h-9 flex items-center justify-center" style={{ background: `${c.accent}14` }}>
                <Icon size={18} weight="duotone" color={c.accent} />
              </div>
            </div>
            <div className="border-t border-[#e4e4e7] pt-3 mt-4">
              <div className="font-heading text-2xl font-black tracking-tighter" style={{ color: c.accent }}>
                {c.count}
              </div>
              <div className="text-[10px] text-[#52525b] font-mono-pg">{c.label}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TrackedMock() {
  return (
    <div className="bg-white border border-[#e4e4e7]">
      <div className="p-4 border-b border-[#e4e4e7] flex items-start justify-between">
        <div>
          <div className="pg-overline text-[#52525b]">PB Tech</div>
          <div className="font-bold text-sm mt-1">ASUS Vivobook Go 15 OLED Laptop</div>
          <div className="text-[11px] text-[#52525b] font-mono-pg mt-1">Bought 14 Feb · 24 days of price protection left</div>
        </div>
        <div className="text-right">
          <div className="pg-overline">Paid</div>
          <div className="font-mono-pg text-sm font-bold mt-0.5">$1,499.00</div>
        </div>
      </div>
      <div className="p-4 grid grid-cols-3 gap-3 text-center">
        <div>
          <div className="pg-overline">Today</div>
          <div className="font-mono-pg text-sm font-bold mt-1">$1,499.00</div>
        </div>
        <div>
          <div className="pg-overline">Lowest</div>
          <div className="font-mono-pg text-sm font-bold mt-1">$1,499.00</div>
        </div>
        <div>
          <div className="pg-overline">Status</div>
          <div className="font-mono-pg text-[11px] font-bold mt-1 text-[#52525b]">No drop yet</div>
        </div>
      </div>
      <div className="border-t border-[#e4e4e7] bg-[#0a0a0a] text-white p-3 text-center text-[11px] font-bold uppercase tracking-widest">
        Watching daily · we'll email if the price moves
      </div>
    </div>
  );
}

function RefundMomentMock() {
  return (
    <div className="space-y-3">
      <div className="bg-white border border-[#16A34A] p-4 flex items-start gap-3" data-testid="refund-mock-alert">
        <div className="w-9 h-9 bg-[#16A34A] text-white flex items-center justify-center flex-shrink-0">
          <ArrowDown size={18} weight="bold" />
        </div>
        <div className="flex-1">
          <div className="pg-overline text-[#16A34A]">Price drop detected</div>
          <div className="font-bold text-sm mt-0.5">Sony WH-1000XM5 dropped $80</div>
          <div className="text-[11px] text-[#52525b] mt-1">Inside JB Hi-Fi's 14-day price-protection window · you bought 6 days ago</div>
        </div>
      </div>
      <div className="bg-white border border-[#e4e4e7] p-4">
        <div className="pg-overline">AI-drafted claim email</div>
        <div className="text-[11px] font-mono-pg text-[#52525b] mt-2">To: web@jbhifi.co.nz</div>
        <div className="text-[11px] font-mono-pg text-[#52525b]">Subject: Price adjustment request - order #JBH-2891</div>
        <div className="mt-3 text-sm text-[#0a0a0a] leading-relaxed">
          Hi JB Hi-Fi team, I bought the Sony WH-1000XM5 from you on 12 February for $549. I've noticed the same model is now listed at $469. Would you be able to honour the $80 price adjustment under your published policy? Order number and receipt attached. Thanks heaps.
        </div>
        <div className="mt-4 flex gap-2">
          <div className="bg-[#002FA7] text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 inline-flex items-center gap-1">
            <EnvelopeSimple size={12} weight="bold" /> Send the claim
          </div>
          <div className="border border-[#0a0a0a] text-[11px] font-bold uppercase tracking-widest px-3 py-1.5">
            Copy
          </div>
        </div>
      </div>
    </div>
  );
}

function WishlistMock() {
  return (
    <div className="space-y-3">
      <div className="bg-white border border-[#e4e4e7] p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="pg-overline text-[#52525b]">JB Hi-Fi · Wishlist</div>
            <div className="font-bold text-sm mt-1 truncate">LG 65" C4 OLED TV</div>
          </div>
          <Heart size={16} weight="duotone" className="text-[#16A34A] flex-shrink-0" />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4 text-center">
          <div>
            <div className="pg-overline">Today</div>
            <div className="font-mono-pg text-sm font-bold mt-1">$2,799</div>
          </div>
          <div>
            <div className="pg-overline text-[#16A34A]">Target</div>
            <div className="font-mono-pg text-sm font-bold mt-1 text-[#16A34A]">$2,400</div>
          </div>
        </div>
        <div className="mt-3 bg-[#f7f7f8] border border-[#e4e4e7] p-2 text-[11px] text-[#52525b] text-center">
          $399 away from your target · we'll email the moment it lands
        </div>
      </div>
      <div className="bg-white border border-[#002FA7]/40 p-3 flex items-start gap-2.5">
        <Sparkle size={16} weight="fill" className="text-[#002FA7] flex-shrink-0 mt-0.5" />
        <div className="text-[11px] text-[#0a0a0a] leading-relaxed">
          <strong>Spotted at PB Tech:</strong> same model listed at $2,649. Want us to watch both?
        </div>
      </div>
    </div>
  );
}

function VaultMock() {
  const items = [
    { name: "Bosch dishwasher SMS6", warranty: "Warranty: 3 of 5 years left", value: "$1,899" },
    { name: "Samsung 65\" QLED TV", warranty: "Warranty: 22 months left", value: "$2,499" },
    { name: "Dyson V15 Detect", warranty: "Warranty: 4 months left", value: "$1,299" },
  ];
  return (
    <div className="space-y-3">
      <div className="bg-[#0a0a0a] text-white p-4 flex items-center justify-between">
        <div>
          <div className="pg-overline text-white/60">Home asset register</div>
          <div className="font-heading text-2xl font-black tracking-tighter mt-1">$8,394 insurable</div>
        </div>
        <div className="text-[10px] font-bold tracking-widest uppercase bg-[#D97706] px-2 py-1">
          Export to CSV
        </div>
      </div>
      {items.map((it) => (
        <div key={it.name} className="bg-white border border-[#e4e4e7] p-3 flex items-center justify-between">
          <div className="min-w-0">
            <div className="font-bold text-sm truncate">{it.name}</div>
            <div className="text-[11px] text-[#52525b] font-mono-pg mt-0.5">{it.warranty}</div>
          </div>
          <div className="font-mono-pg text-sm font-bold flex-shrink-0 ml-3">{it.value}</div>
        </div>
      ))}
    </div>
  );
}

function PromoBonusMock() {
  return (
    <div className="space-y-3">
      <div className="bg-[#F0FDF4] border border-[#16A34A] p-4 flex items-start gap-3">
        <div className="w-9 h-9 bg-[#16A34A] text-white flex items-center justify-center flex-shrink-0">
          <TagSimple size={18} weight="bold" />
        </div>
        <div>
          <div className="pg-overline text-[#16A34A]">Promo code available · save $550</div>
          <div className="font-bold text-sm mt-1">
            Code: <span className="bg-[#0a0a0a] text-white px-2 py-0.5 ml-1 font-mono-pg">PRICEBUSTER</span>
          </div>
          <div className="text-[11px] text-[#52525b] mt-1">Drops the ASUS Vivobook to $2,119.32 at PB Tech checkout</div>
        </div>
      </div>
      <div className="bg-[#FFFBEB] border border-[#D97706] p-4 flex items-start gap-3">
        <div className="w-9 h-9 bg-[#D97706] text-white flex items-center justify-center flex-shrink-0">
          <Gift size={18} weight="bold" />
        </div>
        <div>
          <div className="pg-overline text-[#D97706]">Bonus included · worth $499</div>
          <div className="font-bold text-sm mt-1">Free Sonos Era 100 speaker with this Samsung TV</div>
          <div className="text-[11px] text-[#52525b] mt-1">Listed price doesn't change · easy to miss without us</div>
        </div>
      </div>
    </div>
  );
}

function SaleEndingMock() {
  const items = [
    { ret: "Harvey Norman", name: "Bosch Series 6 Dishwasher", days: 1, was: 1899, now: 1499 },
    { ret: "JB Hi-Fi", name: "Sonos Beam (Gen 2)", days: 3, was: 749, now: 599 },
  ];
  return (
    <div className="bg-[#FFFBEB] border border-[#D97706]">
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          <Clock size={14} weight="bold" className="text-[#D97706]" />
          <div className="pg-overline text-[#D97706]">Sales ending soon · 2 items</div>
        </div>
      </div>
      <div className="px-4 pb-4 pt-2 space-y-2">
        {items.map((i) => (
          <div key={i.name} className="bg-white border border-[#e4e4e7] p-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="pg-overline text-[#52525b]">{i.ret}</div>
              <div className="font-bold text-sm mt-0.5 truncate">{i.name}</div>
              <div className="text-[11px] text-[#52525b] mt-1">
                <span className="line-through">${i.was}</span> now ${i.now}
              </div>
            </div>
            <div className="text-[10px] font-bold tracking-widest text-white bg-[#D97706] px-2 py-1 whitespace-nowrap">
              {i.days === 1 ? "ENDS TODAY" : `${i.days}D LEFT`}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuietMock() {
  return (
    <div className="space-y-3">
      <div className="bg-white border border-[#e4e4e7] p-4 flex items-start gap-3">
        <Bell size={20} weight="duotone" className="text-[#002FA7] mt-0.5 flex-shrink-0" />
        <div>
          <div className="pg-overline text-[#002FA7]">When we email</div>
          <ul className="mt-3 space-y-2 text-sm text-[#0a0a0a]">
            <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-[#16A34A] flex-shrink-0 mt-0.5" /> A tracked item dropped below what you paid</li>
            <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-[#16A34A] flex-shrink-0 mt-0.5" /> A wishlist item hit your target price</li>
            <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-[#16A34A] flex-shrink-0 mt-0.5" /> A bonus or promo code appeared</li>
            <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-[#16A34A] flex-shrink-0 mt-0.5" /> A warranty is 30 days from expiring</li>
          </ul>
        </div>
      </div>
      <div className="bg-[#0a0a0a] text-white p-3 text-center text-[11px] font-bold uppercase tracking-widest">
        Nothing else · no daily digests · no upsells in your inbox
      </div>
    </div>
  );
}
